import PropTypes from 'prop-types';
import React from 'react';
import {
	StyledBackgroundImage,
	StyledBrandCard,
	StyledImageWrapper,
	StyledLabel,
	StyledLogoImage,
} from './styled-brand-card';

const { forwardRef, useCallback } = React;

const propTypes = {
	link: PropTypes.string.isRequired,
	ariaLabel: PropTypes.string,
	backgroundImage: PropTypes.shape({
		alt: PropTypes.string,
		dataTestId: PropTypes.string,
		isLazy: PropTypes.bool,
		imageSrc: PropTypes.string,
		fallbackSource: PropTypes.string,
	}),
	className: PropTypes.string,
	clickCallback: PropTypes.func,
	dataAnalyticsId: PropTypes.string,
	dataTestId: PropTypes.string,
	label: PropTypes.string,
	logoImage: PropTypes.shape({
		alt: PropTypes.string,
		dataTestId: PropTypes.string,
		isLazy: PropTypes.bool,
		imageSrc: PropTypes.string,
		fallbackSource: PropTypes.string,
	}),
};

const defaultProps = {
	ariaLabel: undefined,
	backgroundImage: {},
	className: 'fuse-brand-card',
	clickCallback: undefined,
	dataAnalyticsId: undefined,
	dataTestId: 'fuse-brand-card-test-id',
	label: '',
	logoImage: {},
};

const BrandCard = forwardRef(
	(
		{
			link,
			ariaLabel,
			backgroundImage,
			className,
			clickCallback,
			dataAnalyticsId,
			dataTestId,
			label,
			logoImage,
			...others
		},
		ref,
	) => {
		const {
			alt: backgroundImageAltText,
			dataTestId: backgroundImageTestId,
			isLazy: backgroundImageIsLazy,
			imageSrc: backgroundImageLink,
			fallbackSource: backgroundImageFallbackSource,
		} = backgroundImage;
		const {
			alt: logoImageAltText,
			dataTestId: logoImageTestId,
			isLazy: logoImageIsLazy,
			imageSrc: logoImageLink,
			fallbackSource: logoImageFallbackSource,
		} = logoImage;
		const isLogoOnly =
			(logoImageLink || logoImageFallbackSource) &&
			!(backgroundImageLink || backgroundImageFallbackSource);
		const clickHandler = useCallback(
			(e) => {
				if (typeof clickCallback === 'function') clickCallback(e, ref);
			},
			[clickCallback],
		);

		return (
			<StyledBrandCard
				{...others}
				aria-label={ariaLabel}
				className={className}
				data-analyticsid={dataAnalyticsId}
				data-testid={dataTestId}
				href={link}
				onClick={clickHandler}
				ref={ref}
			>
				{(backgroundImageLink ||
					logoImageLink ||
					backgroundImageFallbackSource ||
					logoImageFallbackSource) && (
					<StyledImageWrapper isLogoOnly={isLogoOnly}>
						{(backgroundImageLink || backgroundImageFallbackSource) && (
							<StyledBackgroundImage
								alt={backgroundImageAltText}
								dataTestId={backgroundImageTestId}
								fallbackSource={backgroundImageLink}
								isLazy={backgroundImageIsLazy}
								{...backgroundImage}
							/>
						)}
						{(logoImageLink || logoImageFallbackSource) && (
							<StyledLogoImage
								alt={logoImageAltText}
								dataTestId={logoImageTestId}
								fallbackSource={logoImageLink}
								isLazy={logoImageIsLazy}
								isLogoOnly={isLogoOnly}
								{...logoImage}
							/>
						)}
					</StyledImageWrapper>
				)}
				{label && <StyledLabel isLogoOnly={isLogoOnly}>{label}</StyledLabel>}
			</StyledBrandCard>
		);
	},
);

BrandCard.propTypes = propTypes;
BrandCard.defaultProps = defaultProps;

export { BrandCard };

export default BrandCard;
